import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Button,
  ConfirmDialog,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  unwrapTrpcData,
  useAuthorization,
} from '@org/ui';
import { trpc } from '@org/clients/school';

type LevelOption = { levelId: string; name: string | null };
type AbilityRow = { abilityId: string; levelId: string; text: string; sortOrder: number };

export const AbilitiesList: React.FC = () => {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const { hasPermission } = useAuthorization();
  const canDelete = hasPermission('levels:delete');

  const [selectedLevelId, setSelectedLevelId] = React.useState<string | null>(null);
  const [newText, setNewText] = React.useState('');
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editingText, setEditingText] = React.useState('');
  const [pendingDeleteId, setPendingDeleteId] = React.useState<string | null>(null);

  const levelsQuery = trpc.levels.list.useQuery();
  const levelOptions = React.useMemo<LevelOption[]>(
    () => (unwrapTrpcData(levelsQuery.data) ?? []) as LevelOption[],
    [levelsQuery.data],
  );

  const abilitiesQuery = trpc.abilities.listByLevel.useQuery(
    { levelId: selectedLevelId ?? '' },
    { enabled: !!selectedLevelId },
  );
  const abilities = React.useMemo<AbilityRow[]>(
    () => (unwrapTrpcData(abilitiesQuery.data) ?? []) as AbilityRow[],
    [abilitiesQuery.data],
  );

  const invalidate = () => {
    if (selectedLevelId) {
      utils.abilities.listByLevel.invalidate({ levelId: selectedLevelId });
    }
  };

  const createMutation = trpc.abilities.create.useMutation({
    onSuccess: () => {
      setNewText('');
      invalidate();
      toast.success(t('abilities.toasts.createSuccess'));
    },
    onError: () => toast.error(t('abilities.toasts.error')),
  });

  const updateMutation = trpc.abilities.update.useMutation({
    onSuccess: () => {
      setEditingId(null);
      setEditingText('');
      invalidate();
      toast.success(t('abilities.toasts.updateSuccess'));
    },
    onError: () => toast.error(t('abilities.toasts.error')),
  });

  const deleteMutation = trpc.abilities.delete.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success(t('abilities.toasts.deleteSuccess'));
    },
    onError: () => toast.error(t('abilities.toasts.error')),
  });

  const reorderMutation = trpc.abilities.reorder.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success(t('abilities.toasts.reorderSuccess'));
    },
    onError: () => toast.error(t('abilities.toasts.error')),
  });

  const handleAdd = () => {
    if (!selectedLevelId || !newText.trim()) return;
    createMutation.mutate({ levelId: selectedLevelId, text: newText.trim() });
  };

  const startEdit = (ability: AbilityRow) => {
    setEditingId(ability.abilityId);
    setEditingText(ability.text);
  };

  const handleSaveEdit = () => {
    if (!editingId || !editingText.trim()) return;
    updateMutation.mutate({ abilityId: editingId, text: editingText.trim() });
  };

  const handleDelete = (abilityId: string) => {
    setPendingDeleteId(abilityId);
  };

  const confirmDelete = () => {
    if (!pendingDeleteId) return;
    deleteMutation.mutate({ abilityId: pendingDeleteId });
    setPendingDeleteId(null);
  };

  const move = (index: number, direction: -1 | 1) => {
    if (!selectedLevelId) return;
    const target = index + direction;
    if (target < 0 || target >= abilities.length) return;
    const ids = abilities.map((a) => a.abilityId);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderMutation.mutate({ levelId: selectedLevelId, abilityIds: ids });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">{t('abilities.title')}</h1>
        <p className="text-sm text-gray-600">{t('abilities.description')}</p>
      </div>

      <div className="max-w-sm space-y-1">
        <label className="text-sm font-medium text-gray-700">{t('abilities.selectLevel')}</label>
        <Select value={selectedLevelId ?? undefined} onValueChange={(value) => setSelectedLevelId(value)}>
          <SelectTrigger>
            <SelectValue placeholder={t('abilities.selectLevelPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {levelOptions.map((level) => (
              <SelectItem key={level.levelId} value={level.levelId}>
                {level.name ?? level.levelId}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!selectedLevelId ? (
        <p className="text-sm text-gray-500">{t('abilities.selectLevelPrompt')}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-2">
            <Input
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder={t('abilities.addPlaceholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
            />
            <Button onClick={handleAdd} disabled={!newText.trim() || createMutation.isPending}>
              {t('abilities.add')}
            </Button>
          </div>

          {abilities.length === 0 ? (
            <p className="text-sm text-gray-500">{t('abilities.empty')}</p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {abilities.map((ability, index) => (
                <li key={ability.abilityId} className="flex items-center gap-3 px-3 py-2">
                  <div className="flex flex-col">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => move(index, -1)}
                      disabled={index === 0 || reorderMutation.isPending}
                      title={t('abilities.moveUp')}
                      className="h-5 px-1 text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30"
                    >
                      &#9650;
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => move(index, 1)}
                      disabled={index === abilities.length - 1 || reorderMutation.isPending}
                      title={t('abilities.moveDown')}
                      className="h-5 px-1 text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30"
                    >
                      &#9660;
                    </Button>
                  </div>

                  {editingId === ability.abilityId ? (
                    <>
                      <Input
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit();
                        }}
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={!editingText.trim() || updateMutation.isPending}
                      >
                        {t('abilities.save')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                        {t('abilities.cancel')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-gray-700">{ability.text}</span>
                      <Button size="sm" variant="ghost" onClick={() => startEdit(ability)}>
                        {t('abilities.edit')}
                      </Button>
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(ability.abilityId)}
                          disabled={deleteMutation.isPending}
                        >
                          {t('abilities.delete')}
                        </Button>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <ConfirmDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteId(null);
        }}
        description={t('abilities.deleteConfirm')}
        confirmLabel={t('abilities.delete')}
        destructive
        onConfirm={confirmDelete}
      />
    </div>
  );
};
